import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import {
  propEq,
  pipe,
  path,
  pathOr,
  map,
  set,
  lensPath,
  values as rValues,
  flatten,
  filter,
  isEmpty,
  complement,
  anyPass,
  prop,
  ifElse,
  isNil,
  find,
  allPass,
  length
} from 'ramda'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'

import { noop } from 'lodash'
import { colors, breakpoints, fontWeights } from '@/styles'
import Tooltip from '@/components/popper-tooltip'
import EditIcon from './edit-icon.svg'
import EditDisabledIcon from './edit-disabled-icon.svg'
import { show as showSelectRecipientFormModal } from './add-recipient-form-modal'
import VoidModalContent from './void-modal-content'
import { FieldDropdown } from '../inputs'
import { getFormattedSigners, getFormattedPackageFiles, getPrimaryLandlord } from '../utils'
import { showDocumentModal } from '../modal'

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  border-bottom: 1px solid ${colors.grey88};
  padding-bottom: 24px;
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
  }
`

const FormWrapper = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

const DropdownColorScheme = styled.span`
  display: inline-block;
  width: 8px;

  height: 100%;
  margin-right: 8px;
  border-radius: 2px;
  background: ${props => props.color};
  flex-shrink: 0;
`
const UserWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${props => props.maxWidth || 190}px;
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const DropdownName = styled.p`
  margin: 0;
  font-weight: ${fontWeights.medium};
  font-size: 16px;
  line-height: 20px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const DropdownEmail = styled.p`
  margin: 0;
  font-weight: ${fontWeights.regular};
  font-size: 12px;
  line-height: 16px;
  color: ${colors.grey56};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const AdjustedFieldDropdown = styled(FieldDropdown)`
  height: 48px;
  width: 100%;
`

const StyledImage = styled.img`
  cursor: pointer;
  margin-left: 10px;
`

const TooltipWrapper = styled.div`
  p {
    max-width: 220px;
    margin: 0;
    padding: 0;
  }
`

const FullText = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
`

const StyledTooltip = styled(Tooltip)`
  > button {
    width: 100%;
  }
`

const getContractdocuments = (signingStatus, user) => {
  const updatedDocuments = pipe(
    pathOr([], ['contract_documents']),
    map(set(lensPath(['recipient_signing_status']), signingStatus))
  )(user)

  return updatedDocuments
}
const isNotNil = complement(isNil)

const RecipientComponent = ({
  recipient,
  contractId,
  items,
  setItems,
  signees,
  setSignees = noop,
  fileMetadata,
  setFileMetadata = noop,
  currentUser,
  setUser,
  currentDocument,
  packageFiles,
  recipients
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { email, first_name, last_name } = recipient
  const showEdit = pipe(complement(path(['contract_documents', 0, 'is_creator'])))(recipient)
  const isApplicant = prop('is_applicant', recipient)
  const document = pipe(
    pathOr([], ['contract_documents']),
    ifElse(
      () => isNotNil(currentDocument),
      find(propEq('listing_contract_package_file_id', Number(currentDocument))),
      path([0])
    )
  )(recipient)

  const color = document?.metadata?.is_creator ? '#70AAFF' : document?.metadata?.color || '#F7D250'
  const initialStatus = pipe(
    pathOr([], ['contract_documents']),
    ifElse(
      () => complement(isNil(currentDocument)),
      find(propEq('listing_contract_package_file_id', Number(currentDocument))),
      path([0])
    ),
    pathOr('signature_required', ['recipient_signing_status'])
  )(recipient)

  const initialValues = {
    recipient_signing_status: initialStatus
  }
  const handleEdit = () =>
    showSelectRecipientFormModal({
      contractId,
      user: recipient,
      editing: true,
      signees,
      setSignees,
      fileMetadata,
      setFileMetadata,
      currentUser,
      setUser,
      items
    })

  const hasItems = pipe(
    rValues,
    flatten,
    filter(
      allPass([
        propEq('recipient_id', recipient.recipient_id),
        anyPass([propEq('inputType', 'signature'), propEq('inputType', 'initials')])
      ])
    ),
    complement(isEmpty)
  )(items)

  const sentDocuments = pipe(filter(propEq('state_machine', 'sent')))(packageFiles)
  const hasSentDocument = pipe(complement(isEmpty))(sentDocuments)

  const handleTenancyAgreementDelete = async fileId => {
    const { response } = await dispatch.contract.removeTenancyAgreement({
      contractId,
      fileId
    })
    return response?.ok
  }

  const handlePackageFileDelete = async fileId => {
    const { response } = await dispatch.contract.removePackageFile({
      contractId,
      fileId
    })
    return response?.ok
  }

  const handleConfirm = async values => {
    const pageRequests = pipe(
      map(async value => {
        const voidFunction =
          value?.document_type === 'tenancy_agreement'
            ? handleTenancyAgreementDelete
            : handlePackageFileDelete
        await voidFunction(value?.id)
      })
    )(sentDocuments)
    const results = await Promise.all(pageRequests)
    if (filter(Boolean, results)) {
      await handleAddRecipients(values, recipient)
    }
  }

  const tooltipText = hasSentDocument
    ? t(
        'l.manage_recipients.has_sent_document.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, their status cannot be modified. Please void the contract and resend to modify.'
      )
    : t(
        'l.contract_manager.no_sent_document.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, their status cannot be modified. Please delete these fields to modify.'
      )

  const handleOnSubmit = (values, { setSubmitting, setFieldValue }) => {
    setSubmitting(true)

    if (hasSentDocument && values.recipient_signing_status === 'signature_required') {
      const confirmCta =
        length(sentDocuments) > 1
          ? t('l.contract_edit.void_all.cta', 'Void all')
          : t('l.contract_edit.void.cta', 'Void')

      const handleCancel = () => setFieldValue('recipient_signing_status', initialStatus)
      showDocumentModal({
        title: t('l.contract_edit.are_you_sure.title', 'Are you sure?'),
        content: (
          <VoidModalContent
            first_name={first_name}
            last_name={last_name}
            sentDocuments={sentDocuments}
          />
        ),
        confirmCta: confirmCta,
        cancelCta: t('common.cancel', 'Cancel'),
        backgroundColor: colors.red96,
        confirmTheme: 'delete',
        confirmCtaIcon: 'void',
        cancelFn: handleCancel,
        confirmFn: () => handleConfirm(values)
      })
    } else {
      handleAddRecipients(values, recipient)
    }

    setSubmitting(false)
  }

  const handleAddRecipients = async (values, recipient) => {
    dispatch.loading.setLoading()
    const signer = {
      email: recipient.email,
      phone_number: recipient.phone_number,
      first_name: recipient.first_name,
      last_name: recipient.last_name,
      dob: recipient.dob,
      application_id: recipient.application_id,
      user_id: recipient.user_id,
      contract_documents: getContractdocuments(values.recipient_signing_status, recipient)
    }

    await dispatch.contract.addRecipient({
      signer,
      contractId
    })

    const response = await dispatch.contract.getPackage({ contractId: contractId })
    const formattedSigners = getFormattedSigners(response?.body?.signers)
    const formattedFiles = getFormattedPackageFiles(response?.body?.package_files)

    setSignees(formattedSigners)
    setFileMetadata({
      ...fileMetadata,
      package_files: formattedFiles,
      signer: response?.body?.signers
    })

    if (
      currentUser.props.recipient_id === recipient.recipient_id &&
      values.recipient_signing_status === 'email_notification_required'
    ) {
      const primaryLandlord = getPrimaryLandlord(recipients, signees)
      pipe(set(lensPath(['props']), primaryLandlord), setUser)(currentUser)
    }

    dispatch.loading.stopLoading()
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleOnSubmit} enableReinitialize>
      {({ handleSubmit }) => {
        return (
          <FormWrapper>
            <Container>
              <ContentWrapper>
                <DropdownColorScheme color={color} condensed={email} />
                <UserWrapper>
                  <NameWrapper>
                    <DropdownName>{`${first_name} ${last_name}`}</DropdownName>

                    {showEdit &&
                      (isApplicant ? (
                        <StyledTooltip
                          enabled={true}
                          size="medium"
                          content={
                            <TooltipWrapper>
                              <FullText>
                                {t(
                                  'l.contract_edit.add_recipients.disabled_edit.text',
                                  'As this recipient was added from the applicant list, their information is automatically pulled from their application and cannot be modified.'
                                )}
                              </FullText>
                            </TooltipWrapper>
                          }
                          placement={'top'}
                        >
                          <StyledImage src={EditDisabledIcon} alt="icon" />
                        </StyledTooltip>
                      ) : (
                        <StyledImage src={EditIcon} alt="icon" onClick={handleEdit} />
                      ))}
                  </NameWrapper>
                  {email && <DropdownEmail>{email}</DropdownEmail>}
                </UserWrapper>
              </ContentWrapper>

              {hasItems ? (
                <StyledTooltip
                  enabled={true}
                  size="medium"
                  content={
                    <TooltipWrapper>
                      <FullText>{tooltipText}</FullText>
                    </TooltipWrapper>
                  }
                  placement={'top'}
                >
                  <AdjustedFieldDropdown
                    id="recipient_signing_status"
                    name="recipient_signing_status"
                    placeholder={t(
                      'l.contract_edit.add_recipients.signing_status.title',
                      'Signing status'
                    )}
                    options={[
                      {
                        value: 'signature_required',
                        label: t('l.listing.edit.signature_required.text', 'Signature Required')
                      },
                      {
                        value: 'email_notification_required',
                        label: t('l.listing.edit.email_notification_required.text', 'Email a copy')
                      }
                    ]}
                    onChange={handleSubmit}
                    isDisabled={true}
                    fullWidth={true}
                  />
                </StyledTooltip>
              ) : (
                <AdjustedFieldDropdown
                  id="recipient_signing_status"
                  name="recipient_signing_status"
                  placeholder={t(
                    'l.contract_edit.add_recipients.signing_status.title',
                    'Signing status'
                  )}
                  options={[
                    {
                      value: 'signature_required',
                      label: t('l.listing.edit.signature_required.text', 'Signature Required')
                    },
                    {
                      value: 'email_notification_required',
                      label: t('l.listing.edit.email_notification_required.text', 'Email a copy')
                    }
                  ]}
                  onChange={handleSubmit}
                />
              )}
            </Container>
          </FormWrapper>
        )
      }}
    </Formik>
  )
}

export default RecipientComponent
